/**
 * t302u: the web side of offline register marks (packages/data/registerOutbox). A tap goes straight to the server
 * when there's signal and nothing for that register is waiting; otherwise (or when the send can't get through) it is
 * saved in the user's IndexedDB outbox with the tap time, shown at once, and sent when the connection is back (same
 * triggers as the run and clock outboxes, lib/runOutbox).
 */
import { useMemo, useSyncExternalStore } from 'react';
import { createRegisterOutbox, opsDataKeys, pendingMarksFor, registerOutboxStore, type RegisterOpMark, type RegisterOutboxOp } from '@tradeoutlet/data';
import { ApiError, classifyOutcome, type ChecklistRunDetail, type PendingMark } from '@tradeoutlet/shared';
import { api } from './api';
import { hubDbName, indexedDbStore } from './idbStore';
import { queryClient } from './queryClient';
import { useUi } from './store';
import { connection } from './connection';
import { uuid } from './uuid';

export const registerOutbox = createRegisterOutbox({
  store: registerOutboxStore(userId => indexedDbStore(hubDbName(userId))),
  uuid,
  isOnline: () => connection.isDeviceOnline(), // t574u: navigator.onLine, unless the API has answered since
  onSent: (op, run) => {
    if (run) queryClient.setQueryData(opsDataKeys.run(op.workspaceId, op.runId), run);
    void queryClient.invalidateQueries({ queryKey: ['ops', op.workspaceId] });
  },
});

export function useRegisterOps(): RegisterOutboxOp[] {
  return useSyncExternalStore(registerOutbox.subscribe, registerOutbox.ops, registerOutbox.ops);
}
/** This register's taps still waiting to send, for applyPendingMarks. */
export function usePendingMarks(runId: string): { pending: PendingMark[]; waiting: number; failed: RegisterOutboxOp[] } {
  const ops = useRegisterOps();
  return useMemo(() => {
    const mine = ops.filter(o => o.runId === runId);
    return { pending: pendingMarksFor(mine, runId), waiting: mine.filter(o => o.state === 'queued' || o.state === 'sending').length, failed: mine.filter(o => o.state === 'failed' || o.state === 'conflict') };
  }, [ops, runId]);
}

export type NewMark = Omit<RegisterOpMark, 'client_mark_id' | 'device_marked_at'>;

/**
 * Save marks on a register. Online with nothing waiting for it: sent now and the answer is the run as the server has
 * it. Otherwise, or when the send can't get through, queued (`{ queued: true }`). Refusals throw.
 */
export async function submitMarks(ws: string, runId: string, label: string, marks: NewMark[]): Promise<{ queued: true } | { queued: false; run: ChecklistRunDetail & { changed?: number } }> {
  const at = new Date().toISOString();
  const full: RegisterOpMark[] = marks.map(m => ({ ...m, client_mark_id: uuid(), device_marked_at: at }));
  const key = uuid();
  const waiting = registerOutbox.ops().some(o => o.runId === runId && o.state !== 'failed' && o.state !== 'conflict');
  if (connection.isDeviceOnline() && !waiting) {
    try {
      const run = await api.ops.markRegister(ws, runId, full, key) as ChecklistRunDetail & { changed?: number };
      queryClient.setQueryData(opsDataKeys.run(ws, runId), run);
      void queryClient.invalidateQueries({ queryKey: ['ops', ws], predicate: q => q.queryKey[2] !== 'run' });
      return { queued: false, run };
    } catch (e) {
      // Only "couldn't get through" falls back to the outbox; the same key and client_mark_ids make a landed send count once.
      if (classifyOutcome(e instanceof ApiError ? e.status : null) !== 'retry') throw e;
    }
  }
  await registerOutbox.enqueue({ id: key, workspaceId: ws, runId, label: `${label} · ${marks.length} ${marks.length === 1 ? 'mark' : 'marks'}`, marks: full });
  return { queued: true };
}

// A batch the server refused: say so once (the offline bar turns red until it's dealt with).
const told = new Set<string>();
registerOutbox.subscribe(() => {
  for (const o of registerOutbox.ops()) {
    if ((o.state === 'failed' || o.state === 'conflict') && !told.has(o.id)) { told.add(o.id); useUi.getState().toast(`Register marks not sent: ${o.error ?? 'refused'}`, 'error'); }
    if (o.state === 'queued') told.delete(o.id);
  }
});
